import { of, switchMap, tap, startWith, debounceTime, merge, fromEvent, distinctUntilChanged, map, delay, filter, first, combineLatest } from 'rxjs';

import { replaceViewContainer, renderTemplate, getParentAttr } from './ui.js';
import { getCollectionMetadata, findSectionsInCollection, addDocument } from './knowledge-api.js';
import { userInfo$ } from './http.js';
import { buildWorkMonitor } from './working.js';

export function createCollectionView(colId) {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('collection-view');
            replaceViewContainer(root);

            let saveMonitor = buildWorkMonitor();
            let saveIndicator$ = saveMonitor.showElements('.saving');
            let alertSavingBeforeUnload$ = saveMonitor.alertBeforeUnload();

            let meta$ = getCollectionMetadata(colId).pipe(
                switchMap(meta => userInfo$.pipe(
                    first(),
                    map(userInfo => ({
                        ...meta,
                        is_owner: meta.owner === userInfo.oauth.sub,
                        security_level_enabled: userInfo.security_level_enabled,
                    })),
                )),
            );

            let renderMeta$ = meta$.pipe(
                tap(meta => {
                    root.querySelector('.collection-title').textContent = `${meta.owner}/${meta.name}`;
                    let properties = [
                        ['security-level', meta.security_level],
                        ['document-count', meta.document_count],
                        ['section-count', meta.section_count],
                        ['pending-section-count', meta.pending_section_count],
                        ['pending-file-imports-count', meta.pending_file_imports_count],
                    ];
                    for (let [metaClass, value] of properties) {
                        root.querySelector(`.collection-meta .${metaClass} .value`).textContent = `${value}`;
                    }
                    if (!meta.security_level_enabled) {
                        root.querySelector('.collection-meta .security-level').classList.add('hidden');
                    }
                    if (!meta.is_owner) {
                        root.querySelector('.destruct-collection').classList.add('hidden');
                        root.querySelector('.manage-api-keys').classList.add('hidden');
                        root.querySelector('.add-as-document').classList.add('hidden');
                    }
                }),
            );

            let form = root.querySelector('form');
            let searchContextEl = form.querySelector('.context textarea');

            let sessionKey = `collection-search-context-${colId}`;
            let savedContext = sessionStorage.getItem(sessionKey);
            if (savedContext) {
                searchContextEl.value = savedContext;
            }

            let searchContext$ = fromEvent(searchContextEl, 'input').pipe(
                debounceTime(300),
                startWith(undefined),
                map(() => (searchContextEl.value || '').trim()),
                tap(context => sessionStorage.setItem(sessionKey, context)),
                distinctUntilChanged(),
            );

            let nearestSectionsEl = root.querySelector('.nearest-sections');
            
            let nearestSections$ = searchContext$.pipe(
                tap(() => nearestSectionsEl.textContent = 'Loading…'),
                switchMap(context => findSectionsInCollection(colId, context)),
            );

            let renderNearestSections$ = nearestSections$.pipe(
                tap(nearestSections => {
                    nearestSectionsEl.innerHTML = '';
                    for (let sec of nearestSections) {
                        let secEl = renderTemplate('nearest-section-widget');
                        secEl.setAttribute('data-section-id', sec.id);
                        secEl.setAttribute('data-doc-id', sec.doc_id);
                        secEl.querySelector('.text').textContent = sec.text;
                        secEl.querySelector('.cosine-distance').textContent = sec.cos_dist.toFixed(3);
                        if (sec.doc_source_uri) {
                            let srcEl = secEl.querySelector('.source-uri');
                            srcEl.textContent = sec.doc_source_uri;
                            srcEl.href = sec.doc_source_uri;
                            secEl.querySelector('.source-uri-container').classList.remove('hidden');
                        }
                        secEl.querySelector('.edit-section').addEventListener('click', handleEditSection, false);
                        secEl.querySelector('.edit-document').addEventListener('click', handleEditDocument, false);
                        nearestSectionsEl.appendChild(secEl);
                    }
                }),
            );

            let addAsDocumentAction$ = fromEvent(root.querySelector('.add-as-document'), 'click').pipe(
                map(() => (searchContextEl.value || '').trim()),
                filter(content => !!content),
                switchMap(content => saveMonitor.zone(addDocument(colId, {
                    sections: [content],
                }))),
                tap(() => form.reset()),
            );

            let destructCollectionClick$ = fromEvent(root.querySelector('.destruct-collection'), 'click').pipe(
                tap(() => window.location.href = `#collection/${colId}/destruct`),
            );

            let viewDocumentsClick$ = fromEvent(root.querySelector('.view-documents'), 'click').pipe(
                tap(() => window.location.href = `#collection/${colId}/documents`),
            );

            let manageApiKeysClick$ = fromEvent(root.querySelector('.manage-api-keys'), 'click').pipe(
                tap(() => window.location.href = `#collection/${colId}/api-keys`),
            );

            let manageSharingClick$ = fromEvent(root.querySelector('.manage-sharing'), 'click').pipe(
                tap(() => window.location.href = `#collection/${colId}/sharing`),
            );

            return merge(
                saveIndicator$,
                alertSavingBeforeUnload$,
                renderMeta$,
                renderNearestSections$,
                addAsDocumentAction$,
                destructCollectionClick$,
                viewDocumentsClick$,
                manageApiKeysClick$,
                manageSharingClick$,
            );
        }),
    );
}

function handleEditSection(event) {
    let sectionId = getParentAttr(event.target, 'data-section-id');
    window.location.href = `#section/${sectionId}`;
}

function handleEditDocument(event) {
    let docId = getParentAttr(event.target, 'data-doc-id');
    window.location.href = `#document/${docId}`;
}
